import { RudderAnalytics } from '@rudderstack/analytics-js';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { addBreadcrumb } from '@sentry/react';

import { GetAppConfigDocument, GetAppConfigQuery } from '@ocx/graphql';
import {
  Event,
  AnalyticsInstance,
  RudderStackPageOptions,
  RudderStackUserTraits,
  RudderStackConsentPreferences,
  rudderStackCookieCategories,
  RudderStackConsentGetter,
} from './rudderStack.types';
import { EnvConfiguration } from '../../modules/configuration/EnvConfiguration';
import { getHrefWithPathname, maskHref, maskPathname } from './url.utils';
import { withTryCatch } from '../error-handling/utils';

const rudderStack = new RudderAnalytics();

export const getPageOptions = (): RudderStackPageOptions => {
  const maskedPathname = maskPathname(window.location.pathname);
  const maskedHref = maskHref(window.location.href);

  return {
    path: maskedPathname,
    tab_url: maskedHref,
    url: maskedHref,
  };
};

const getIsValidConfig = (): boolean => {
  const { writeKey, dataPlaneUrl } = EnvConfiguration.rudderStack;
  return !!writeKey && !!dataPlaneUrl;
};

export const getCustomPageOptions = (pathname: string): RudderStackPageOptions => {
  const href = getHrefWithPathname(pathname);

  return {
    path: pathname,
    tab_url: href,
    url: href,
  };
};

const consent = withTryCatch((params: RudderStackConsentPreferences) => {
  rudderStack.consent({
    consentManagement: {
      // Provide the consent IDs obtained from your consent provider.
      allowedConsentIds: params.allowedConsentIds, // Required for custom provider
      deniedConsentIds: params.deniedConsentIds, // Required for custom provider
    },
    trackConsent: true,
    storage: {
      type: 'localStorage',
    },

    // We should collect events even before obtaining consent, to have a complete picture of user behavior leading up to the consent decision.
    // RudderStack will handle these pre-consent events according to the consent preferences provided here.
    discardPreConsentEvents: false,
  });
});

const init = withTryCatch((params: { consentGetter?: RudderStackConsentGetter } = {}) => {
  const { writeKey, dataPlaneUrl } = EnvConfiguration.rudderStack;
  const isValidConfig = getIsValidConfig();

  if (!isValidConfig) {
    addBreadcrumb({
      message: 'RudderStack -> Configuration is missing or invalid.',
    });
    return;
  }

  // Load RudderStack in non-consent mode if cookie consent provider is not provided.
  if (!params.consentGetter) {
    addBreadcrumb({
      message: 'RudderStack -> Loading in non-consent mode.',
    });
    rudderStack.load(writeKey, dataPlaneUrl, {
      storage: {
        type: 'localStorage',
      },
    });
    return;
  }

  // Load RudderStack in pre-consent mode if cookie consent provider is enabled and preConsentEnabled is true
  addBreadcrumb({
    message: 'RudderStack -> Loading in pre-consent mode.',
  });
  rudderStack.load(writeKey, dataPlaneUrl, {
    consentManagement: {
      enabled: true,
      provider: 'custom',
    },
    preConsent: {
      enabled: true,
      storage: {
        strategy: 'session', // Optional; other accepted values are "none", "session"
      },
      events: {
        delivery: 'buffer', // Optional; other accepted value is "immediate"
      },
    },
  });

  params.consentGetter({
    onConsent: (params) => {
      addBreadcrumb({
        message: 'RudderStack -> Cookie Consent obtained, sending consent update to RudderStack.',
      });
      consent({
        allowedConsentIds: params.allowedConsentIds,
        deniedConsentIds: params.deniedConsentIds,
      });
    },
    onError: () => {
      addBreadcrumb({
        message: 'RudderStack -> Error obtaining consent from cookie consent provider.',
      });
      consent({
        allowedConsentIds: [rudderStackCookieCategories.necessary],
        deniedConsentIds: [
          rudderStackCookieCategories.preferences,
          rudderStackCookieCategories.analytics,
          rudderStackCookieCategories.marketing,
        ],
      });
    },
  });
});

const triggerEvent = withTryCatch((...[event, params]: Event): void => {
  const pageOptions = getPageOptions();
  rudderStack.track(event, params, { page: pageOptions });
});

/**
 * Triggers a location event in RudderStack. This function is used to track page views.
 *
 * @param [pathname] - The pathname of the page to track. If provided, it tracks a page view for a different page
 *                     than the current one. This is useful for tracking page views of modals or overlays
 *                     that don't have their own URL, like a survey modal opened on top of the home page.
 */
const triggerLocation = withTryCatch((pathname?: string) => {
  const pageOptions = pathname ? getCustomPageOptions(pathname) : getPageOptions();
  rudderStack.page(pageOptions);
});

const identify = withTryCatch((userId: string, userIdentity: RudderStackUserTraits) => {
  const pageOptions = getPageOptions();
  rudderStack.identify(userId, userIdentity, { page: pageOptions });
});

export const reset = withTryCatch(() => {
  rudderStack.reset();
});

export const getIsRudderStackEnabledFromApolloCache = (apolloClient: ApolloClient<NormalizedCacheObject>) => {
  // It's needed to pass apolloClient as parameter because of circular dependency that happens when importing apolloClient in this file
  const response = apolloClient.readQuery<GetAppConfigQuery>({ query: GetAppConfigDocument });
  return response?.appConfig?.rudderStack?.enabled ?? false;
};

export const getRudderStackInstance = (enabled: boolean): AnalyticsInstance => {
  if (!enabled) {
    return {
      triggerEvent: () => {},
      triggerLocation: () => {},
      identify: () => {},
      init: () => {},
      consent: () => {},
      reset: () => {},
    };
  }

  return { triggerEvent, triggerLocation, identify, init, consent, reset };
};
